import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Send, X, Clock, Check, ChevronLeft, Shield, MessageSquare, Loader2, Search, RotateCcw, Filter, ImagePlus, Camera, Paperclip, FileText, Download } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const CATEGORIES = ['Workplace Issue', 'Salary', 'Harassment', 'Suggestion', 'Other'];

const STATUS_COLORS = {
  open: 'bg-blue-100 text-blue-700',
  replied: 'bg-violet-100 text-violet-700',
  closed: 'bg-gray-100 text-gray-500',
};

const CATEGORY_COLORS = {
  'Workplace Issue': 'bg-amber-50 text-amber-700 border-amber-200',
  'Salary': 'bg-green-50 text-green-700 border-green-200',
  'Harassment': 'bg-red-50 text-red-700 border-red-200',
  'Suggestion': 'bg-sky-50 text-sky-700 border-sky-200',
  'Other': 'bg-gray-50 text-gray-600 border-gray-200',
};

/* Renders inline images + downloadable file chips for a message/reply,
   supporting both new `attachments` arrays and the legacy single `image_id`. */
const AttachmentBubble = ({ msg }) => {
  const atts = (msg.attachments && msg.attachments.length)
    ? msg.attachments
    : (msg.image_id ? [{ id: msg.image_id, kind: 'image', name: 'photo' }] : []);
  if (!atts.length) return null;
  return (
    <div className="space-y-2 mb-2" data-testid="attachment-bubble">
      {atts.map(a => a.kind === 'image' ? (
        <a key={a.id} href={`${API}/api/employee-portal/grievances/image/${a.id}`} target="_blank" rel="noopener noreferrer" className="block">
          <img src={`${API}/api/employee-portal/grievances/image/${a.id}`} alt={a.name || 'photo'}
               className="rounded-lg max-h-56 w-auto object-cover border border-black/10" />
        </a>
      ) : (
        <a key={a.id} href={`${API}/api/employee-portal/grievances/attachment/${a.id}?download=1`} target="_blank" rel="noopener noreferrer"
           className="flex items-center gap-2 bg-white/90 border rounded-lg px-3 py-2 text-xs text-gray-700 hover:bg-white max-w-[240px]">
          <FileText className="h-4 w-4 text-rose-500 flex-shrink-0" />
          <span className="truncate">{a.name || 'Attachment'}</span>
          <Download className="h-3.5 w-3.5 ml-auto flex-shrink-0 text-gray-400" />
        </a>
      ))}
    </div>
  );
};

const PortalGrievance = () => {
  const [grievances, setGrievances] = useState([]);
  const [isHR, setIsHR] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [activeChat, setActiveChat] = useState(null);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/employee-portal/grievances`, { headers });
      setGrievances(res.data.grievances || []);
      setIsHR(res.data.is_hr || false);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('portal_token');
        navigate('/employee-portal/login');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { load(); }, [load]);

  if (activeChat) {
    return (
      <ChatView
        grievanceId={activeChat}
        token={token}
        isHR={isHR}
        onBack={() => { setActiveChat(null); load(); }}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600" />
      </div>
    );
  }

  const filtered = search
    ? grievances.filter(g =>
        g.employee_name?.toLowerCase().includes(search.toLowerCase()) ||
        g.employee_code?.toLowerCase().includes(search.toLowerCase()) ||
        g.message?.toLowerCase().includes(search.toLowerCase()) ||
        g.category?.toLowerCase().includes(search.toLowerCase()) ||
        g.department?.toLowerCase().includes(search.toLowerCase())
      )
    : grievances;

  // HR View
  if (isHR) {
    const counts = {
      all: grievances.length,
      open: grievances.filter(g => g.status === 'open').length,
      replied: grievances.filter(g => g.status === 'replied').length,
    };

    return (
      <div className="space-y-4 max-w-lg mx-auto" data-testid="portal-grievance-hr">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Employee Messages</h2>
          <p className="text-xs text-gray-400 mt-0.5">Messages received from employees</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
            <p className="text-xl font-bold text-gray-900">{counts.all}</p>
            <p className="text-[10px] text-gray-400">Total</p>
          </div>
          <div className="bg-blue-50 rounded-xl p-3 text-center border border-blue-100">
            <p className="text-xl font-bold text-blue-700">{counts.open}</p>
            <p className="text-[10px] text-gray-400">Open</p>
          </div>
          <div className="bg-violet-50 rounded-xl p-3 text-center border border-violet-100">
            <p className="text-xl font-bold text-violet-700">{counts.replied}</p>
            <p className="text-[10px] text-gray-400">Replied</p>
          </div>
        </div>

        {/* Search */}
        {grievances.length > 0 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search messages..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9"
              data-testid="hr-grievance-search"
            />
          </div>
        )}

        {/* Message List */}
        {filtered.length === 0 ? (
          <Card className="border-dashed border-0 shadow-sm">
            <CardContent className="p-10 text-center">
              <MessageSquare className="h-10 w-10 mx-auto mb-3 text-gray-200" />
              <p className="text-sm text-gray-400">{search ? 'No messages match your search' : 'No messages from employees yet'}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filtered.map(g => (
              <Card
                key={g.id}
                className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => setActiveChat(g.id)}
                data-testid={`hr-grievance-${g.id}`}
              >
                <CardContent className="p-3.5">
                  <div className="flex items-start gap-2.5">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <MessageSquare className="h-4 w-4 text-gray-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-sm font-semibold text-gray-900">{g.employee_name}</p>
                          {g.employee_code && <span className="text-[10px] text-gray-400">{g.employee_code}</span>}
                        </div>
                        <Badge className={`text-[9px] px-1.5 py-0 flex-shrink-0 ${STATUS_COLORS[g.status] || STATUS_COLORS.open}`}>
                          {g.status === 'replied' ? 'Replied' : g.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">{g.message}</p>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <Badge variant="outline" className={`text-[8px] px-1.5 py-0 ${CATEGORY_COLORS[g.category] || CATEGORY_COLORS.Other}`}>{g.category}</Badge>
                        {g.department && <span className="text-[9px] text-gray-400">{g.department}</span>}
                        <span className="text-[9px] text-gray-400">
                          {new Date(g.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                        </span>
                        {g.reply_count > 0 && <span className="text-[9px] text-violet-500">{g.reply_count} {g.reply_count === 1 ? 'reply' : 'replies'}</span>}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Employee View (non-HR)
  return (
    <div className="space-y-4 max-w-lg mx-auto" data-testid="portal-grievance">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-bold text-gray-900">Message HR</h2>
        <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={() => setShowNew(true)} data-testid="new-message-btn">
          <Plus className="h-4 w-4 mr-1" /> New Message
        </Button>
      </div>

      <div className="flex items-center gap-2 px-3 py-2.5 bg-violet-50 rounded-xl border border-violet-100">
        <Shield className="h-4 w-4 text-violet-500 flex-shrink-0" />
        <p className="text-[11px] text-violet-700 leading-relaxed">
          <span className="font-semibold">Message HR</span> — Send anonymous or identified messages. You choose whether to reveal your name when composing a new message.
        </p>
      </div>

      {grievances.length === 0 ? (
        <Card className="border-dashed border-0 shadow-sm">
          <CardContent className="p-10 text-center">
            <MessageSquare className="h-10 w-10 mx-auto mb-3 text-gray-200" />
            <p className="text-sm text-gray-400">No messages yet</p>
            <p className="text-[10px] text-gray-300 mt-1">Tap "New Message" to send your first message to HR</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {grievances.map(g => (
            <Card
              key={g.id}
              className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setActiveChat(g.id)}
              data-testid={`grievance-${g.id}`}
            >
              <CardContent className="p-3.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    <Badge className={`text-[9px] px-1.5 py-0 ${STATUS_COLORS[g.status] || STATUS_COLORS.open}`}>
                      {g.status === 'replied' ? 'HR Replied' : g.status}
                    </Badge>
                    <Badge className={`text-[9px] px-1.5 py-0 ${g.is_anonymous !== false ? 'bg-violet-50 text-violet-500' : 'bg-sky-50 text-sky-600'}`}>
                      {g.is_anonymous !== false ? 'Anonymous' : 'Named'}
                    </Badge>
                  </div>
                  <span className="text-[10px] text-gray-400">
                    {new Date(g.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-2 line-clamp-2">{g.message}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className={`text-[8px] px-1.5 py-0 ${CATEGORY_COLORS[g.category] || CATEGORY_COLORS.Other}`}>{g.category}</Badge>
                  {(g.replies || []).length > 0 && (
                    <span className="text-[9px] text-violet-500">{g.replies.length} {g.replies.length === 1 ? 'reply' : 'replies'}</span>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showNew && <NewGrievanceDialog token={token} onClose={() => setShowNew(false)} onSuccess={() => { setShowNew(false); load(); }} />}
    </div>
  );
};


/* ── Chat View ────────────────── */
const ChatView = ({ grievanceId, token, isHR, onBack }) => {
  const [grievance, setGrievance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [uploadingReply, setUploadingReply] = useState(false);
  const replyFileRef = useRef(null);
  const replyCamRef = useRef(null);
  const [closing, setClosing] = useState(false);
  const [reopening, setReopening] = useState(false);
  const scrollRef = useRef(null);
  const headers = { Authorization: `Bearer ${token}` };

  const uploadReplyFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploadingReply(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/api/employee-portal/grievances/upload-attachment`, fd, { headers });
        setReplyAttachments(prev => [...prev, { ...res.data, previewUrl: res.data.kind === 'image' ? URL.createObjectURL(file) : null }]);
      }
    } catch { alert('Failed to upload file'); }
    finally {
      setUploadingReply(false);
      if (replyFileRef.current) replyFileRef.current.value = '';
      if (replyCamRef.current) replyCamRef.current.value = '';
    }
  };
  const removeReplyAttachment = (id) => setReplyAttachments(prev => prev.filter(a => a.id !== id));

  const loadChat = async () => {
    try {
      const res = await axios.get(`${API}/api/employee-portal/grievances/${grievanceId}`, { headers });
      setGrievance(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadChat(); }, [grievanceId]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [grievance?.replies]);

  const handleSendReply = async () => {
    if (!reply.trim() && replyAttachments.length === 0) return;
    setSending(true);
    try {
      const attachments = replyAttachments.map(({ id, kind, name, content_type }) => ({ id, kind, name, content_type }));
      await axios.post(`${API}/api/employee-portal/grievances/${grievanceId}/reply`, { message: reply.trim(), attachments }, { headers });
      setReply('');
      setReplyAttachments([]);
      loadChat();
    } catch { alert('Failed to send'); }
    finally { setSending(false); }
  };

  const handleClose = async () => {
    setClosing(true);
    try {
      await axios.patch(`${API}/api/employee-portal/grievances/${grievanceId}/close`, {}, { headers });
      loadChat();
    } catch { alert('Failed to close'); }
    finally { setClosing(false); }
  };

  const handleReopen = async () => {
    setReopening(true);
    try {
      await axios.patch(`${API}/api/employee-portal/grievances/${grievanceId}/reopen`, {}, { headers });
      loadChat();
    } catch { alert('Failed to reopen'); }
    finally { setReopening(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-gray-300" /></div>;
  if (!grievance) return <p className="text-center text-gray-400 py-16">Not found</p>;

  const isClosed = grievance.status === 'closed';

  return (
    <div className="max-w-lg mx-auto flex flex-col h-[calc(100vh-80px)]" data-testid="grievance-chat">
      {/* Header */}
      <div className="flex items-center gap-2 pb-3 border-b">
        <Button variant="ghost" size="sm" onClick={onBack} className="h-8 w-8 p-0" data-testid="chat-back-btn">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            {isHR && grievance.employee_name && (
              <>
                <p className="text-sm font-semibold text-gray-900">{grievance.employee_name}</p>
                {grievance.employee_code && <span className="text-[10px] text-gray-400">{grievance.employee_code}</span>}
              </>
            )}
            <Badge className={`text-[9px] px-1.5 py-0 ${STATUS_COLORS[grievance.status]}`}>
              {grievance.status === 'replied' ? (isHR ? 'Replied' : 'HR Replied') : grievance.status}
            </Badge>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <Badge variant="outline" className={`text-[8px] px-1.5 py-0 ${CATEGORY_COLORS[grievance.category] || ''}`}>{grievance.category}</Badge>
            {isHR && grievance.department && <span className="text-[9px] text-gray-400">{grievance.department}</span>}
          </div>
        </div>
        {isHR && !isClosed && (
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleClose} disabled={closing} data-testid="close-chat-btn">
            {closing ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="h-3 w-3 mr-1" /> Close</>}
          </Button>
        )}
        {isHR && isClosed && (
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleReopen} disabled={reopening} data-testid="reopen-chat-btn">
            {reopening ? <Loader2 className="h-3 w-3 animate-spin" /> : <><RotateCcw className="h-3 w-3 mr-1" /> Reopen</>}
          </Button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto py-4 space-y-3" ref={scrollRef}>
        {/* Original message */}
        <div className={`flex ${isHR ? 'justify-start' : 'justify-end'}`}>
          <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${isHR ? 'bg-gray-100 rounded-tl-md' : 'bg-sky-500 text-white rounded-tr-md'}`}>
            <p className={`text-[10px] font-semibold mb-1 ${isHR ? 'text-gray-500' : 'text-sky-100'}`}>
              {isHR ? 'Employee' : 'You'}
            </p>
            <AttachmentBubble msg={grievance} />
            {grievance.message && <p className={`text-sm leading-relaxed ${isHR ? 'text-gray-800' : ''}`}>{grievance.message}</p>}
            <p className={`text-[10px] mt-2 ${isHR ? 'text-gray-400' : 'text-sky-200'}`}>
              {new Date(grievance.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
              {' '}{new Date(grievance.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
            </p>
          </div>
        </div>

        {/* Replies */}
        {(grievance.replies || []).map(r => {
          const isAdminMsg = r.sender_type === 'admin';
          // For HR view: admin msgs on right, employee msgs on left
          // For employee view: admin msgs on left, employee msgs on right
          const isRight = isHR ? isAdminMsg : !isAdminMsg;
          return (
            <div key={r.id} className={`flex ${isRight ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                isRight
                  ? (isHR ? 'bg-violet-500 text-white rounded-tr-md' : 'bg-sky-500 text-white rounded-tr-md')
                  : 'bg-gray-100 rounded-tl-md'
              }`}>
                <p className={`text-[10px] font-semibold mb-1 ${isRight ? (isHR ? 'text-violet-100' : 'text-sky-100') : 'text-gray-500'}`}>
                  {isAdminMsg ? (isHR ? (r.sender_name || 'You') : 'HR') : (isHR ? 'Employee' : 'You')}
                </p>
                <AttachmentBubble msg={r} />
                {r.message && <p className={`text-sm leading-relaxed ${isRight ? '' : 'text-gray-800'}`}>{r.message}</p>}
                <p className={`text-[10px] mt-2 ${isRight ? (isHR ? 'text-violet-200' : 'text-sky-200') : 'text-gray-400'}`}>
                  {new Date(r.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                  {' '}{new Date(r.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}
                </p>
              </div>
            </div>
          );
        })}

        {isClosed && (
          <div className="text-center py-2">
            <Badge className="text-[10px] bg-gray-100 text-gray-500">Conversation closed</Badge>
          </div>
        )}
      </div>

      {/* Reply box */}
      {!isClosed && (
        <div className="border-t pt-3 pb-2">
          {replyAttachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2" data-testid="reply-attachments-preview">
              {replyAttachments.map(a => (
                <div key={a.id} className="relative">
                  {a.kind === 'image' && a.previewUrl ? (
                    <img src={a.previewUrl} alt="preview" className="h-16 w-16 object-cover rounded-lg border" />
                  ) : (
                    <div className="h-16 w-24 rounded-lg border bg-gray-50 flex flex-col items-center justify-center px-1">
                      <FileText className="h-5 w-5 text-rose-500" />
                      <span className="text-[9px] text-gray-500 truncate w-full text-center mt-0.5">{a.name}</span>
                    </div>
                  )}
                  <button onClick={() => removeReplyAttachment(a.id)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 shadow"
                    data-testid={`reply-attach-remove-${a.id}`}>
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            <input ref={replyFileRef} type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx" multiple className="hidden"
              onChange={e => uploadReplyFiles(e.target.files)} data-testid="reply-file-input" />
            <input ref={replyCamRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={e => uploadReplyFiles(e.target.files)} data-testid="reply-camera-input" />
            <Button type="button" variant="outline" className="h-11 w-10 p-0 flex-shrink-0"
              onClick={() => replyFileRef.current?.click()} disabled={uploadingReply || sending}
              title="Attach photo or file" data-testid="reply-attach-btn">
              {uploadingReply ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
            </Button>
            <Button type="button" variant="outline" className="h-11 w-10 p-0 flex-shrink-0 sm:hidden"
              onClick={() => replyCamRef.current?.click()} disabled={uploadingReply || sending}
              title="Take a photo" data-testid="reply-camera-btn">
              <Camera className="h-4 w-4" />
            </Button>
            <Textarea
              value={reply}
              onChange={e => setReply(e.target.value)}
              placeholder={isHR ? "Reply to employee..." : "Type your reply..."}
              rows={2}
              className="flex-1 min-h-[44px] max-h-[100px] resize-none text-sm"
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendReply(); } }}
              data-testid="chat-reply-input"
            />
            <Button
              className={`h-11 px-4 ${isHR ? 'bg-violet-600 hover:bg-violet-700' : 'bg-sky-600 hover:bg-sky-700'}`}
              onClick={handleSendReply}
              disabled={(!reply.trim() && replyAttachments.length === 0) || sending}
              data-testid="chat-send-btn"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};


/* ── New Grievance Dialog (non-HR only) ────────────────── */
const NewGrievanceDialog = ({ token, onClose, onSuccess }) => {
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    setError('');
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/api/employee-portal/grievances/upload-attachment`, fd, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAttachments(prev => [...prev, { ...res.data, previewUrl: res.data.kind === 'image' ? URL.createObjectURL(file) : null }]);
      }
    } catch (e) { setError(e.response?.data?.detail || 'Failed to upload file'); }
    finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
      if (camRef.current) camRef.current.value = '';
    }
  };
  const removeAttachment = (id) => setAttachments(prev => prev.filter(a => a.id !== id));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category) { setError('Please select a category'); return; }
    if (!message.trim() && attachments.length === 0) { setError('Add a message or an attachment'); return; }
    setSubmitting(true);
    setError('');
    try {
      const atts = attachments.map(({ id, kind, name, content_type }) => ({ id, kind, name, content_type }));
      await axios.post(`${API}/api/employee-portal/grievances`, {
        category, message: message.trim(), is_anonymous: isAnonymous, attachments: atts
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="new-grievance-dialog">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto z-10">
        <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h3 className="text-base font-bold text-gray-900">New Message to HR</h3>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100"><X className="h-5 w-5 text-gray-400" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Anonymous toggle */}
          <div
            className={`flex items-center justify-between px-3.5 py-3 rounded-xl border cursor-pointer transition-all ${
              isAnonymous ? 'bg-violet-50 border-violet-200' : 'bg-sky-50 border-sky-200'
            }`}
            onClick={() => setIsAnonymous(!isAnonymous)}
            data-testid="anonymous-toggle"
          >
            <div className="flex items-center gap-2.5">
              <Shield className={`h-4 w-4 flex-shrink-0 ${isAnonymous ? 'text-violet-500' : 'text-sky-500'}`} />
              <div>
                <p className={`text-xs font-semibold ${isAnonymous ? 'text-violet-700' : 'text-sky-700'}`}>
                  {isAnonymous ? 'Sending Anonymously' : 'Sending with your name'}
                </p>
                <p className="text-[10px] text-gray-500 mt-0.5">
                  {isAnonymous ? 'HR will not see your identity' : 'HR will see your name and employee code'}
                </p>
              </div>
            </div>
            <div className={`w-10 rounded-full p-0.5 transition-colors ${isAnonymous ? 'bg-violet-400' : 'bg-sky-400'}`}>
              <div className="rounded-full bg-white shadow transition-transform"
                style={{ width: '18px', height: '18px', transform: isAnonymous ? 'translateX(0)' : 'translateX(18px)' }}
              />
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium">Category *</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="mt-1" data-testid="grievance-category-select">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent className="z-[100]">
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-medium">Message *</Label>
            <Textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Describe your concern..."
              rows={4}
              className="mt-1"
              data-testid="grievance-message-input"
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Photos & Files (optional)</Label>
            <input ref={fileRef} type="file" accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.ppt,.pptx" multiple className="hidden"
              onChange={e => uploadFiles(e.target.files)} data-testid="grievance-file-input" />
            <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={e => uploadFiles(e.target.files)} data-testid="grievance-camera-input" />
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2 mb-1" data-testid="grievance-attachments-preview">
                {attachments.map(a => (
                  <div key={a.id} className="relative">
                    {a.kind === 'image' && a.previewUrl ? (
                      <img src={a.previewUrl} alt="preview" className="h-20 w-20 object-cover rounded-lg border" />
                    ) : (
                      <div className="h-20 w-24 rounded-lg border bg-gray-50 flex flex-col items-center justify-center px-1">
                        <FileText className="h-6 w-6 text-rose-500" />
                        <span className="text-[9px] text-gray-500 truncate w-full text-center mt-0.5">{a.name}</span>
                      </div>
                    )}
                    <button type="button" onClick={() => removeAttachment(a.id)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 shadow"
                      data-testid={`grievance-attach-remove-${a.id}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2 mt-1">
              <Button type="button" variant="outline" className="flex-1"
                onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="grievance-attach-btn">
                {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</> : <><Paperclip className="h-4 w-4 mr-2" /> Add Photo / File</>}
              </Button>
              <Button type="button" variant="outline" className="flex-1 sm:hidden"
                onClick={() => camRef.current?.click()} disabled={uploading} data-testid="grievance-camera-btn">
                <Camera className="h-4 w-4 mr-2" /> Camera
              </Button>
            </div>
          </div>
          {error && <p className="text-sm text-red-500 text-center">{error}</p>}
          <Button type="submit" className="w-full py-5 bg-sky-600 hover:bg-sky-700" disabled={submitting || uploading || !category || (!message.trim() && attachments.length === 0)} data-testid="submit-grievance-btn">
            {submitting ? 'Sending...' : <><Send className="h-4 w-4 mr-2" /> Send Message</>}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PortalGrievance;
